import { useRef, type CSSProperties } from 'react'
import '@fontsource/tilt-neon'
import { useI18n } from '@/lib/i18n'
import { BgVideo, PhraseSwap, clamp01, pick, rand, useCanvasLoop, useCtaText, usePhraseCycle, type SaverProps } from './shared'

const BOKEH_COLORS = ['255, 43, 214', '34, 211, 238', '251, 191, 36', '139, 92, 246', '255, 120, 80']
const BEAMS = [
  { left: '12%', color: '#ff2bd6', dur: 9, delay: 0 },
  { left: '36%', color: '#22d3ee', dur: 11, delay: -3 },
  { left: '64%', color: '#fbbf24', dur: 8, delay: -5 },
  { left: '88%', color: '#8b5cf6', dur: 10, delay: -1 },
]

interface Bokeh { x: number; y: number; r: number; vx: number; vy: number; color: string; phase: number; a: number }
interface Bubble { x: number; y: number; r: number; vy: number; phase: number }
interface Pop { x: number; y: number; age: number; size: number }

export default function PartySaver({ videoSrc, phrases, intervalSeconds, wakeMethod }: SaverProps) {
  const { t } = useI18n()
  const cta = useCtaText(wakeMethod)
  const phrase = usePhraseCycle(phrases, Math.max(3.5, intervalSeconds * 1.8))
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const glowRef = useRef<HTMLDivElement>(null)
  const state = useRef({ bokeh: [] as Bokeh[], bubbles: [] as Bubble[], pops: [] as Pop[], queue: [] as number[], nextVolley: 2.5 })

  useCanvasLoop(canvasRef, (ctx, w, h, time, dt) => {
    const s = state.current
    const scale = Math.min(w, h) / 1000
    if (s.bokeh.length === 0) {
      for (let i = 0; i < 22; i++) s.bokeh.push({ x: rand(0, w), y: rand(0, h), r: rand(30, 110) * scale, vx: rand(-10, 10), vy: rand(-8, 4), color: pick(BOKEH_COLORS), phase: rand(0, 6.28), a: rand(0.06, 0.16) })
      for (let i = 0; i < 70; i++) s.bubbles.push({ x: rand(0, w), y: rand(h * 0.3, h), r: rand(1.2, 3.6) * Math.max(1, scale), vy: rand(30, 90), phase: rand(0, 6.28) })
    }

    // Out-of-focus party lights
    ctx.globalCompositeOperation = 'lighter'
    for (const b of s.bokeh) {
      b.x += b.vx * dt
      b.y += b.vy * dt
      if (b.x < -b.r) b.x = w + b.r
      if (b.x > w + b.r) b.x = -b.r
      if (b.y < -b.r) b.y = h + b.r
      if (b.y > h + b.r) b.y = -b.r
      const a = b.a * (0.65 + 0.35 * Math.sin(time * 0.8 + b.phase))
      const g = ctx.createRadialGradient(b.x, b.y, 0, b.x, b.y, b.r)
      g.addColorStop(0, `rgba(${b.color}, ${a})`)
      g.addColorStop(0.82, `rgba(${b.color}, ${a * 0.85})`)
      g.addColorStop(1, `rgba(${b.color}, 0)`)
      ctx.fillStyle = g
      ctx.beginPath()
      ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2)
      ctx.fill()
    }

    // Champagne bubbles rising from the bottom, fading out as they climb
    for (const b of s.bubbles) {
      b.y -= b.vy * dt
      const x = b.x + Math.sin(time * 2 + b.phase) * 4
      if (b.y < h * 0.25) { b.y = h + 10; b.x = rand(0, w) }
      const fade = clamp01((b.y - h * 0.25) / (h * 0.4))
      ctx.strokeStyle = `rgba(255, 220, 140, ${(0.55 * fade).toFixed(3)})`
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.arc(x, b.y, b.r, 0, Math.PI * 2)
      ctx.stroke()
      ctx.fillStyle = `rgba(255, 245, 210, ${(0.8 * fade).toFixed(3)})`
      ctx.beginPath()
      ctx.arc(x - b.r * 0.35, b.y - b.r * 0.35, b.r * 0.35, 0, Math.PI * 2)
      ctx.fill()
    }

    // Glitter
    for (let i = 0; i < 26; i++) {
      const seed = i * 97.13
      const tw = Math.sin(time * (1.5 + (i % 5) * 0.4) + seed)
      if (tw < 0.75) continue
      const x = ((Math.sin(seed) * 0.5 + 0.5) * w + time * 6 * ((i % 3) - 1)) % w
      const y = (Math.cos(seed * 1.7) * 0.5 + 0.5) * h
      const size = (tw - 0.75) * 4 * 9 * Math.max(1, scale)
      star(ctx, x, y, size, `rgba(255, 255, 255, ${((tw - 0.75) * 4).toFixed(3)})`)
    }

    // Paparazzi: volleys of 2-4 flashes, a frame or two apart
    if (time > s.nextVolley) {
      s.nextVolley = time + rand(3.5, 6.5)
      const n = 2 + Math.floor(Math.random() * 3)
      for (let i = 0; i < n; i++) s.queue.push(time + i * rand(0.12, 0.26))
    }
    while (s.queue.length && s.queue[0] <= time) {
      s.queue.shift()
      const edge = Math.random()
      const x = edge < 0.5 ? (Math.random() < 0.5 ? rand(0.03, 0.2) : rand(0.8, 0.97)) * w : rand(0.05, 0.95) * w
      const y = edge < 0.5 ? rand(0.1, 0.9) * h : (Math.random() < 0.5 ? rand(0.04, 0.16) : rand(0.84, 0.96)) * h
      s.pops.push({ x, y, age: 0, size: rand(90, 170) * Math.max(0.6, scale) })
    }
    let glow = 0
    s.pops = s.pops.filter((p) => p.age < 0.45)
    for (const p of s.pops) {
      p.age += dt
      const k = Math.exp(-p.age * 11)
      glow = Math.max(glow, k)
      const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size)
      g.addColorStop(0, `rgba(255,255,255,${k})`)
      g.addColorStop(0.2, `rgba(220,235,255,${k * 0.6})`)
      g.addColorStop(1, 'rgba(200,220,255,0)')
      ctx.fillStyle = g
      ctx.beginPath()
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2)
      ctx.fill()
      star(ctx, p.x, p.y, p.size * 0.9, `rgba(255,255,255,${k})`)
    }
    ctx.globalCompositeOperation = 'source-over'
    if (glowRef.current) glowRef.current.style.opacity = String(glow * 0.18)
  })

  const title = t('screensaver.hero.party')
  // One letter on its own flicker cycle, like a tired neon tube
  const flickerAt = Math.max(0, title.length - 3)

  return (
    <div className="ss-party absolute inset-0 overflow-hidden" style={{ background: 'radial-gradient(ellipse at 50% 120%, #3b0a45 0%, #14031f 50%, #05010a 100%)', fontFamily: "'Geist Variable', sans-serif" }}>
      <style>{PARTY_CSS}</style>
      <BgVideo src={videoSrc} style={{ opacity: 0.3, filter: 'saturate(1.6) brightness(0.6) contrast(1.2)' }} />
      <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(5,1,10,0.55) 0%, rgba(5,1,10,0.1) 45%, rgba(5,1,10,0.75) 100%)' }} />

      {BEAMS.map((b) => (
        // Blur on the wrapper: a filter runs before clip-path on the same element, which would leave hard edges
        <div key={b.left} className="ss-party-beam absolute top-[-6vh]" style={{ left: b.left, animationDuration: `${b.dur}s`, animationDelay: `${b.delay}s` }}>
          <div className="ss-party-cone h-full w-full" style={{ '--beam': b.color } as CSSProperties} />
        </div>
      ))}
      <div className="ss-party-floor absolute inset-x-0 bottom-0 h-[35%]" />
      <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />
      <div ref={glowRef} className="pointer-events-none absolute inset-0 bg-white" style={{ opacity: 0 }} />

      <div className="absolute inset-0 flex flex-col items-center justify-center px-[5vw] text-center" style={{ fontFamily: "'Tilt Neon', 'Geist Variable', sans-serif" }}>
        <div className="ss-party-kicker mb-[3vmin] text-[3vmin] uppercase tracking-[0.4em]">{t('screensaver.hero.partyKicker')}</div>
        <div className="ss-party-sign relative rounded-[4vmin] px-[6vmin] py-[3vmin]">
          <h1 className="ss-party-title text-[15vmin] leading-none" aria-label={title}>
            {Array.from(title).map((ch, i) => (
              <span key={i} className={i === flickerAt ? 'ss-party-tired' : undefined} style={{ whiteSpace: 'pre' }}>{ch}</span>
            ))}
          </h1>
        </div>
        <div className="ss-party-sub mt-[5vmin] w-full text-[4.2vmin]">
          <PhraseSwap phrase={phrase} />
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-[5vmin] flex justify-center">
        <div className="ss-party-cta rounded-full px-[4vmin] py-[1.6vmin] text-[2.6vmin] font-semibold uppercase tracking-[0.18em] text-white">
          {cta}
        </div>
      </div>
    </div>
  )
}

function star(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, color: string) {
  ctx.fillStyle = color
  ctx.beginPath()
  ctx.moveTo(x, y - size)
  ctx.quadraticCurveTo(x, y, x + size * 0.12, y)
  ctx.quadraticCurveTo(x, y, x, y + size)
  ctx.quadraticCurveTo(x, y, x - size * 0.12, y)
  ctx.quadraticCurveTo(x, y, x, y - size)
  ctx.moveTo(x - size, y)
  ctx.quadraticCurveTo(x, y, x, y - size * 0.12)
  ctx.quadraticCurveTo(x, y, x + size, y)
  ctx.quadraticCurveTo(x, y, x, y + size * 0.12)
  ctx.quadraticCurveTo(x, y, x - size, y)
  ctx.fill()
}

const PINK_GLOW = '0 0 0.04em #fff, 0 0 0.1em #fff, 0 0 0.2em #ff4fd8, 0 0 0.4em #ff2bd6, 0 0 0.8em #ff2bd6, 0 0 1.3em #c026d3'
const PARTY_CSS = `
.ss-party-cone { background: linear-gradient(180deg, var(--beam) 0%, transparent 88%); clip-path: polygon(47% 0, 53% 0, 100% 100%, 0 100%); }
.ss-party-beam { width: 46vmin; height: 135vh; margin-left: -23vmin; transform-origin: 50% 0; filter: blur(14px); mix-blend-mode: screen; opacity: 0.38; will-change: transform; animation-name: ss-party-swing; animation-timing-function: ease-in-out; animation-iteration-count: infinite; animation-direction: alternate; }
@keyframes ss-party-swing { from { transform: rotate(-28deg) } to { transform: rotate(28deg) } }
.ss-party-floor { background: radial-gradient(ellipse at 50% 100%, rgba(255, 43, 214, 0.28), transparent 70%); animation: ss-party-floor 6s ease-in-out infinite alternate; }
@keyframes ss-party-floor { from { filter: hue-rotate(0deg) } to { filter: hue-rotate(160deg) } }
.ss-party-title { color: #ffe9fb; text-shadow: ${PINK_GLOW}; animation: ss-party-on 1.8s steps(1) 1 both, ss-party-hum 9s 2s infinite; }
@keyframes ss-party-on { 0% { opacity: 0.1 } 8% { opacity: 1 } 12% { opacity: 0.2 } 20% { opacity: 1 } 24% { opacity: 0.3 } 30%, 100% { opacity: 1 } }
@keyframes ss-party-hum { 0%, 91%, 93%, 95%, 100% { opacity: 1 } 92%, 94% { opacity: 0.55 } }
.ss-party-tired { animation: ss-party-tired 5.5s steps(1) infinite; }
@keyframes ss-party-tired { 0%, 60%, 63%, 66%, 100% { opacity: 1; text-shadow: inherit } 61%, 64% { opacity: 0.25; text-shadow: none } }
.ss-party-sign { border: 0.5vmin solid #a5f3fc; box-shadow: 0 0 0.6vmin #fff, 0 0 2vmin #22d3ee, 0 0 5vmin #22d3ee, inset 0 0 2vmin #22d3ee, inset 0 0 5vmin rgba(34, 211, 238, 0.35); animation: ss-party-on 1.8s 0.4s steps(1) 1 both; }
.ss-party-kicker { color: #cffafe; text-shadow: 0 0 0.1em #fff, 0 0 0.4em #22d3ee, 0 0 0.9em #22d3ee; }
.ss-party-sub { color: #fef3c7; text-shadow: 0 0 0.1em #fff, 0 0 0.35em #fbbf24, 0 0 0.9em #f59e0b; }
.ss-party-cta { background: rgba(10, 2, 20, 0.55); border: 1px solid rgba(255, 79, 216, 0.7); box-shadow: 0 0 2vmin rgba(255, 43, 214, 0.45), inset 0 0 1.5vmin rgba(255, 43, 214, 0.25); backdrop-filter: blur(6px); animation: ss-party-pulse 2s ease-in-out infinite; }
@keyframes ss-party-pulse { 0%, 100% { box-shadow: 0 0 2vmin rgba(255, 43, 214, 0.45), inset 0 0 1.5vmin rgba(255, 43, 214, 0.25) } 50% { box-shadow: 0 0 4.5vmin rgba(255, 43, 214, 0.8), inset 0 0 2vmin rgba(255, 43, 214, 0.4) } }
`
